/**
 * Feature Flags for Djayma.
 * Toggle via VITE_FF_* env vars or localStorage overrides.
 *
 * Usage:
 *   import { ff } from "@/lib/feature-flags";
 *   if (ff.skeletonScreens) { ... }
 *
 * Runtime override (browser console):
 *   localStorage.setItem("ff_override_skeleton_screens", "false");
 *   location.reload();
 */

interface FeatureFlags {
  /** Show skeleton placeholders instead of spinners */
  skeletonScreens: boolean;
  /** PWA: service worker + offline page + install prompt */
  pwa: boolean;
  /** Dynamic SEO: per-page meta tags, JSON-LD, Open Graph */
  seo: boolean;
  /** Lazy-load images below the fold via IntersectionObserver */
  imageLazyLoading: boolean;
  /** Optimistic UI: update cart/UI instantly before server confirms */
  optimisticUpdates: boolean;
  /** Supabase Realtime: live stock counts and order status */
  realtimeUpdates: boolean;
  /** E2E test hooks: add data-testid attributes to key elements */
  e2eTestIds: boolean;
}

function envFlag(name: string, defaultValue: boolean): boolean {
  // Check localStorage override first (runtime toggle)
  if (typeof window !== "undefined") {
    const override = localStorage.getItem(`ff_override_${name}`);
    if (override === "true") return true;
    if (override === "false") return false;
  }

  // Check VITE_FF_* env var (build-time toggle)
  const envKey = `VITE_FF_${name.toUpperCase()}`;
  const envVal = import.meta.env[envKey];
  if (envVal === "true") return true;
  if (envVal === "false") return false;

  return defaultValue;
}

/** Immutable feature flag snapshot -- read once at module init. */
export const ff: Readonly<FeatureFlags> = Object.freeze({
  skeletonScreens: envFlag("skeleton_screens", true),
  pwa: envFlag("pwa", true),
  seo: envFlag("seo", true),
  imageLazyLoading: envFlag("image_lazy_loading", true),
  optimisticUpdates: envFlag("optimistic_updates", true),
  realtimeUpdates: envFlag("realtime_updates", true),
  e2eTestIds: envFlag("e2e_test_ids", true),
});

/** List all flags with current values -- useful for debug panel */
export function listFlags(): Record<string, boolean> {
  return { ...ff };
}

/** Toggle a single flag at runtime (persists in localStorage) */
export function setFlagOverride(name: keyof FeatureFlags, value: boolean): void {
  localStorage.setItem(`ff_override_${name}`, String(value));
}

/** Clear a single runtime override (reverts to env/default) */
export function clearFlagOverride(name: keyof FeatureFlags): void {
  localStorage.removeItem(`ff_override_${name}`);
}

/** Clear all runtime overrides */
export function clearAllOverrides(): void {
  const keys = Object.keys(ff) as Array<keyof FeatureFlags>;
  for (const key of keys) {
    localStorage.removeItem(`ff_override_${key}`);
  }
}

// Re-export FlagProvider and related exports from the feature-flags module directory
export { FlagProvider } from "./feature-flags/index";
export { useFeatureFlag } from "./feature-flags/index";
export { FeatureGate } from "./feature-flags/index";
export type { FeatureFlag, Role, FlagEvaluationContext } from "./feature-flags/index";
